import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Smartphone, Ticket, Lock, Timer, Copy, Check, Plus, Trash2, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { api } from '../../lib/api';

export default function AdminAccess() {
  const [settings, setSettings] = useState(null);
  const [ttl, setTtl] = useState(30);
  const [coupons, setCoupons] = useState([]);
  const [copied, setCopied] = useState('');
  const [duration, setDuration] = useState(60);
  const [newPwd, setNewPwd] = useState('');
  const [pwdSaved, setPwdSaved] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [err, setErr] = useState('');
  const refreshing = useRef(false);

  const loadSettings = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;
    try {
      const data = await api.get('/api/admin/settings');
      setSettings(data);
      setTtl(data.totp.ttl);
    } catch (e) {
      setErr(e.message);
    } finally {
      refreshing.current = false;
    }
  }, []);

  const loadCoupons = useCallback(async () => {
    try {
      setCoupons(await api.get('/api/admin/coupons'));
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadCoupons();
  }, [loadSettings, loadCoupons]);

  // Compte à rebours local ; on redemande le code au serveur à l'expiration.
  useEffect(() => {
    const id = setInterval(() => {
      setTtl((t) => {
        if (t <= 1) {
          loadSettings();
          return 30;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [loadSettings]);

  const copy = (text, tag) => {
    navigator.clipboard?.writeText(text);
    setCopied(tag);
    setTimeout(() => setCopied(''), 1500);
  };

  const genCoupon = async () => {
    try {
      const coupon = await api.post('/api/admin/coupons', { duration_minutes: duration });
      setCoupons((c) => [coupon, ...c]);
    } catch (e) {
      setErr(e.message);
    }
  };

  const revoke = async (coupon) => {
    try {
      await api.del(`/api/admin/coupons/${coupon.id}`);
      loadCoupons();
    } catch (e) {
      setErr(e.message);
    }
  };

  const rotateTotp = async () => {
    if (!confirmRotate) {
      setConfirmRotate(true);
      setTimeout(() => setConfirmRotate(false), 4000);
      return;
    }
    setConfirmRotate(false);
    try {
      const data = await api.post('/api/admin/settings/rotate_totp');
      setSettings(data);
      setTtl(data.totp.ttl);
    } catch (e) {
      setErr(e.message);
    }
  };

  const savePwd = async () => {
    if (!newPwd.trim()) return;
    try {
      await api.patch('/api/admin/settings', { admin_password: newPwd.trim() });
      setNewPwd('');
      setPwdSaved(true);
    } catch (e) {
      setErr(e.message);
    }
  };

  const fmtLeft = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 'expiré';
    const m = Math.floor(ms / 60000);
    return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
  };

  if (!settings) return <div className="empty">Chargement…</div>;
  const { totp } = settings;

  return (
    <div className="admin-grid">
      {err && <div className="test err span2" style={{ gridColumn: '1/-1' }}>{err}</div>}
      <div className="panel">
        <h3 className="panel-h"><Smartphone size={17} /> Authenticator (TOTP)</h3>
        <p className="panel-p">Scannez ce QR avec Google Authenticator, Authy ou équivalent — ou saisissez le secret à la main. Les invités entreront ensuite leur code à 6 chiffres.</p>
        <div className="qr-enroll">
          <div className="qr-box" dangerouslySetInnerHTML={{ __html: totp.qr_svg }} />
          <div className="qr-enroll-side">
            <div className="live-code col">
              <span className="secret-label">Code valide actuellement</span>
              <div className="live-digits">{totp.current_code}</div>
              <div className="ttl"><Timer size={14} /> expire dans {ttl}s</div>
            </div>
          </div>
        </div>
        <div className="secret-box">
          <span className="secret-label">Secret (saisie manuelle)</span>
          <div className="secret-val">
            <code>{totp.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
            <button className="mini-btn" onClick={() => copy(totp.secret, 'secret')}>{copied === 'secret' ? <Check size={14} /> : <Copy size={14} />}</button>
          </div>
        </div>
        <div className="secret-box">
          <span className="secret-label">URI otpauth</span>
          <div className="secret-val">
            <code className="trunc">{totp.otpauth}</code>
            <button className="mini-btn" onClick={() => copy(totp.otpauth, 'uri')}>{copied === 'uri' ? <Check size={14} /> : <Copy size={14} />}</button>
          </div>
        </div>
        <button className={`btn-ghost danger${confirmRotate ? ' armed' : ''}`} onClick={rotateTotp}>
          <AlertTriangle size={15} /> {confirmRotate ? 'Cliquer à nouveau pour confirmer' : 'Régénérer le secret invités'}
        </button>
      </div>

      <div className="panel">
        <h3 className="panel-h"><Ticket size={17} /> Coupons provisoires</h3>
        <p className="panel-p">Pour les invités sans application authenticator : générez un code à durée limitée, transmettez-le, révoquez-le à tout moment.</p>
        <div className="coupon-gen">
          <label className="field small">
            <span className="field-label">Durée de validité</span>
            <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
              <option value={15}>15 minutes</option>
              <option value={60}>1 heure</option>
              <option value={240}>4 heures</option>
              <option value={720}>12 heures</option>
              <option value={1440}>24 heures</option>
            </select>
          </label>
          <button className="btn-primary" onClick={genCoupon}><Plus size={15} /> Générer un coupon</button>
        </div>
        <div className="coupon-list">
          {coupons.length === 0 && <div className="empty">Aucun coupon actif.</div>}
          {coupons.map((c) => (
            <div key={c.id} className={`coupon${c.usable ? '' : ' expired'}`}>
              <code className="coupon-code">{c.code}</code>
              <span className="coupon-meta">
                {c.revoked ? 'Révoqué' : c.usable ? `Expire dans ${fmtLeft(c.expires_at)}` : 'Expiré'}
                {' · '}{c.uses_count} utilisation{c.uses_count > 1 ? 's' : ''}
              </span>
              <div className="coupon-actions">
                <button className="mini-btn" onClick={() => copy(c.code, c.code)}>{copied === c.code ? <Check size={14} /> : <Copy size={14} />}</button>
                {!c.revoked && <button className="mini-btn danger" onClick={() => revoke(c)}><Trash2 size={14} /></button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="panel span2">
        <h3 className="panel-h"><Lock size={17} /> Compte administrateur</h3>
        <div className="pwd-change">
          <label className="field small grow">
            <span className="field-label">Nouveau mot de passe</span>
            <input type="password" value={newPwd} onChange={(e) => { setNewPwd(e.target.value); setPwdSaved(false); }} placeholder="••••••••" />
          </label>
          <button className="btn-ghost" onClick={savePwd} disabled={!newPwd.trim()}>
            {pwdSaved ? <><Check size={15} /> Enregistré</> : 'Mettre à jour'}
          </button>
        </div>
        <div className="sec-warn slim">
          <ShieldCheck size={15} />
          Le secret TOTP, les coupons et le persona vivent en base côté serveur ; les clés API restent dans
          les credentials Rails et ne transitent jamais par le navigateur. Régénérer le secret invités oblige
          les authenticators déjà enrôlés à se ré-enrôler.
        </div>
      </div>
    </div>
  );
}
